import Auth2 from 'plugin/src/shared/Auth2'
import {
  _persistBroadcastIdentify,
  broadcastLastLoginHostname,
  broadcastToExtensions,
  deleteAllDataInStorage,
  responseIncludesAuth,
  getAccessToken as webGetAccessToken,
  getDeviceId as webGetDeviceId
} from 'web/src/shared/Auth'
import { getDeviceIdOrAccessTokenHash as webGetDeviceIdOrAccessTokenHash } from 'web/src/shared/Auth'
import { isInExtension } from '@mote/helpers/extension'
import { reallyTruthy } from '@mote/helpers/validation'
import { canAccessWebLocalStorage } from '../browser'
import { WEB_STORAGE_PREFIX_KEYS } from '../constants'
import { DiagnosticsLogger } from '../diagnostics'
import { flattenDataFromResponse } from './flattenDataFromResponse'

const inExtension = isInExtension()
const hasLocalStoragePermission = canAccessWebLocalStorage()
const logD = new DiagnosticsLogger('unifiedAuth')

export function isUserOnTier(tier) {
  return Auth2.getProductEnum() === tier
}

export function getMaxRecordingDurationPerTier(tier) {
  return Auth2.getMaxSecondsPerMote(tier)
}

export const getDeviceIdOrAccessTokenHash = () => {
  if (inExtension) {
    return Auth2.getDeviceIdOrAccessTokenHash()
  } else {
    return webGetDeviceIdOrAccessTokenHash()
  }
}

export const getDeviceId = () => {
  if (inExtension) {
    return Auth2.getDeviceId()
  } else {
    return webGetDeviceId()
  }
}

export const getAccessToken = () => {
  if (inExtension) {
    return Auth2.getAccessToken()
  } else {
    return webGetAccessToken()
  }
}

export const getLastUserId = () => {
  const lastUserId = inExtension
    ? Auth2.getLastUserId()
    : getAuthAttribute('lastUserId')

  return lastUserId ?? ''
}

export function getRationing() {
  if (inExtension) {
    return Auth2.getRationing()
  } else {
    if (!hasLocalStoragePermission) return {}

    return {
      canMote: localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}canMote`),
      freeMotesMintedEpoch: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}freeMotesMintedEpoch`
      ),
      totalFreeMotes: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}totalFreeMotes`
      ),
      usedFreeMotes: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}usedFreeMotes`
      ),
      availableFreeMotes: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}availableFreeMotes`
      ),
      isUpgradable: localStorage.getItem(
        `${WEB_STORAGE_PREFIX_KEYS}isUpgradable`
      )
    }
  }
}

export function handleResponseAuthData(response, url) {
  const flattenedData = flattenDataFromResponse(response)

  if (!responseIncludesAuth(flattenedData)) return

  const currentAccessToken = getDeviceIdOrAccessTokenHash()?.accessToken

  if (currentAccessToken !== flattenedData.accessToken) {
    logD.info(`Access token changed, url: ${url}`, {
      oldAccessToken: currentAccessToken,
      newAccessToken: flattenedData.accessToken
    })
  }

  if (inExtension) {
    Auth2.updateCacheAndStorageFromResponse(flattenedData, url)
  } else {
    _persistBroadcastIdentify(flattenedData, true)
    broadcastLastLoginHostname()
  }
}

export function shouldDebitMote() {
  if (inExtension) {
    return Auth2.shouldDebitMote()
  } else {
    if (!hasLocalStoragePermission) return false

    const rationing = reallyTruthy(
      localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}applyFreeMoteRationing`)
    )

    return rationing
  }
}

export function canTrustFreeMotesAllocation() {
  if (inExtension) {
    return Auth2.canTrustFreeMotesAllocation()
  } else {
    if (!hasLocalStoragePermission) return false

    const nextReplenishEpoch = localStorage.getItem(
      `${WEB_STORAGE_PREFIX_KEYS}freeMotesMintedEpoch`
    )

    return new Date() < new Date(nextReplenishEpoch * 1000)
  }
}

export const getAuthAttribute = (attribute = null) => {
  if (!hasLocalStoragePermission) return null
  if (!attribute) return null

  return window.localStorage.getItem(`${WEB_STORAGE_PREFIX_KEYS}${attribute}`)
}

export const forceLogout = () => {
  broadcastToExtensions('logout', {})
  deleteAllDataInStorage()
}
